import React, { FunctionComponent } from "react";
import {
  Box,
  Card,
  Chip,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  useTheme
} from "@mui/material";
import styles from "./styles.module.scss";
import { strings } from "../../localization/LocalizedStrings";
import { CustomLoadingIndicator } from "../CustomLoadingIndicator";
import { Icon, IconType } from "../../icons";

export const SelectChipsListItem: FunctionComponent<ISelectChipsListItemProps> = ({
  className,
  disableGutters,
  iconType,
  title,
  chips,
  isLoading,
  onAdd,
  onChipSelected,
  onDelete,
  helpHref,
  addDisabled,
  removeDisabled
}) => {
  const theme = useTheme();
  return (
    <ListItem className={className} disableGutters={disableGutters}>
      <div className={styles.itemDiv}>
        <Stack direction={"row"} sx={{ width: "100%" }}>
          {iconType && (
            <ListItemIcon sx={{ alignSelf: "center" }}>
              <Icon type={iconType} />
            </ListItemIcon>
          )}
          <Stack
            direction={"column"}
            sx={{ flexGrow: 1, width: "100%" }}
            spacing={helpHref ? 0 : 1}
          >
            <Stack direction={"row"} alignItems={"center"}>
              <ListItemText className={styles.title} disableTypography secondary={title} />
              {helpHref && (
                <Tooltip title={strings.help} arrow disableInteractive>
                  <IconButton sx={{ marginLeft: "4px" }} href={helpHref} target={"_blank"}>
                    <Icon type={IconType.Help} />
                  </IconButton>
                </Tooltip>
              )}
              <Box flexGrow={1} />
            </Stack>
            <Card
              className={styles.chipsCard}
              sx={{
                borderWidth: "1.5px",
                borderColor: theme.palette.background.paper,
                borderStyle: "solid"
              }}
            >
              {chips?.map((chip, index) => (
                <Tooltip
                  key={`tooltip_${title}_${index}`}
                  title={chip.description}
                  slotProps={{ tooltip: { sx: { fontWeight: "normal" } } }}
                  arrow
                  disableInteractive
                >
                  <Box key={`${title}_${index}`} className={styles.chipBox}>
                    <Chip
                      className={styles.chip}
                      label={chip.name}
                      onClick={onChipSelected ? () => onChipSelected(index, chip) : undefined}
                      onDelete={removeDisabled ? undefined : () => onDelete(index, chip)}
                      disabled={isLoading}
                    />
                  </Box>
                </Tooltip>
              ))}
              {!addDisabled && (
                <Tooltip title={strings.add_attribute} arrow disableInteractive>
                  <Box key={"chip_add"} className={styles.chipBox}>
                    <Chip
                      className={styles.addChip}
                      icon={
                        isLoading ? (
                          <CustomLoadingIndicator className={styles.loadingIndicator} />
                        ) : (
                          <Icon type={IconType.AddCircle} />
                        )
                      }
                      disabled={isLoading}
                      onClick={onAdd}
                      sx={{
                        width: "32px",
                        "& .MuiChip-label": {
                          display: "none"
                        },
                        "& .MuiChip-icon": {
                          marginLeft: 0,
                          marginRight: 0,
                          marginTop: "1px"
                        }
                      }}
                    />
                  </Box>
                </Tooltip>
              )}
            </Card>
          </Stack>
        </Stack>
      </div>
    </ListItem>
  );
};

export interface IChipElement {
  id: string;
  name: string;
  description?: string;
}

export interface ISelectChipsListItemProps {
  className?: string;
  disableGutters?: boolean;
  iconType?: IconType;
  title: string;
  chips?: IChipElement[];
  isLoading?: boolean;
  onAdd?: () => void;
  onChipSelected?: (index: number, chip: IChipElement) => void;
  onDelete?: (index: number, chip: IChipElement) => void;
  helpHref?: string;
  addDisabled?: boolean;
  removeDisabled?: boolean;
}
